<script>
  import { onMount, createEventDispatcher } from "svelte";
  import { stoppingFeature, startLocation } from "../state";
  import { findBasinForPoint, getBasinIntelligence, findNearestRiver } from "./utils/mapboxUtils";

  import CloseButton from "./CloseButton.svelte";

  export let vizState;
  export let postRun;

  export let featureGroups = [];
  export let activeFeatureIndex;
  export let totalLength;

  export let startCoordinates;

  let visible = false;
  let screenWidth = 0;

  let currentStoppingFeature = null;
  let currentStartLocation = null;

  // Basin data from spatial lookup
  let basinData = null;
  let basinIntel = null;

  // Nearest river from proximity lookup
  let nearestRiver = null;

  // Spatial lookup - find basin and nearest river when coordinates change
  $: if (startCoordinates && startCoordinates.length === 2) {
    lookupLocation(startCoordinates[0], startCoordinates[1]);
  }

  async function lookupLocation(lng, lat) {
    // Lookup basin (for strategic context)
    basinData = await findBasinForPoint(lng, lat);
    if (basinData && basinData.basin_name) {
      basinIntel = getBasinIntelligence(basinData.basin_name);
    } else {
      basinIntel = null;
    }

    // Lookup nearest named river (for specific identification)
    nearestRiver = await findNearestRiver(lng, lat);
  }

  // Strategic Intelligence Data for Major Ethiopian Rivers (fallback for name matching)
  // Keys are normalized (lowercase) for matching
  const RIVER_INTEL_DATA = {
    abbay: {
      name: "Abbay (Blue Nile)",
      role: "Critical Resource",
      dest: "Mediterranean (via Sudan/Egypt)",
      insight: "Supplies 85% of the Nile's water. Historically defined the Kingdom of Gojjam as a natural fortress.",
      priority: 1
    },
    "blue nile": {
      name: "Abbay (Blue Nile)",
      role: "Critical Resource",
      dest: "Mediterranean (via Sudan/Egypt)",
      insight: "Supplies 85% of the Nile's water. Historically defined the Kingdom of Gojjam as a natural fortress.",
      priority: 1
    },
    tekeze: {
      name: "Tekeze River",
      role: "Strategic Border",
      dest: "Atbarah River (Sudan)",
      insight: "Forms the natural boundary between Tigray and Eritrea. Historically a line of defense.",
      priority: 1
    },
    tekezze: {
      name: "Tekeze River",
      role: "Strategic Border",
      dest: "Atbarah River (Sudan)",
      insight: "Forms the natural boundary between Tigray and Eritrea. Historically a line of defense.",
      priority: 1
    },
    takeze: {
      name: "Tekeze River",
      role: "Strategic Border",
      dest: "Atbarah River (Sudan)",
      insight: "Forms the natural boundary between Tigray and Eritrea. Historically a line of defense.",
      priority: 1
    },
    takazze: {
      name: "Tekeze River",
      role: "Strategic Border",
      dest: "Atbarah River (Sudan)",
      insight: "Forms the natural boundary between Tigray and Eritrea. Historically a line of defense.",
      priority: 1
    },
    atbara: {
      name: "Tekeze River",
      role: "Strategic Border",
      dest: "Atbarah River (Sudan)",
      insight: "Forms the natural boundary between Tigray and Eritrea. Historically a line of defense.",
      priority: 1
    },
    awash: {
      name: "Awash River",
      role: "Economic Lifeline",
      dest: "Terminates in Afar Desert",
      insight: "The only major river entirely contained within Ethiopia. Critical for sugar and cotton agriculture.",
      priority: 2
    },
    omo: {
      name: "Omo River",
      role: "Power & Culture",
      dest: "Lake Turkana (Kenya)",
      insight: "Powerhouse of the Gibe dams. Supports distinct tribal identities in the south.",
      priority: 2
    },
    gibe: {
      name: "Omo River",
      role: "Power & Culture",
      dest: "Lake Turkana (Kenya)",
      insight: "Powerhouse of the Gibe dams. Supports distinct tribal identities in the south.",
      priority: 3
    },
    turkana: {
      name: "Omo River",
      role: "Power & Culture",
      dest: "Lake Turkana (Kenya)",
      insight: "Powerhouse of the Gibe dams. Supports distinct tribal identities in the south.",
      priority: 2
    },
    shebelle: {
      name: "Wabi Shebelle",
      role: "Eastern Artery",
      dest: "Somalia (Indian Ocean)",
      insight: "Critical water source for the Somali region. Often dries up before reaching the ocean.",
      priority: 2
    },
    shebeli: {
      name: "Wabi Shebelle",
      role: "Eastern Artery",
      dest: "Somalia (Indian Ocean)",
      insight: "Critical water source for the Somali region. Often dries up before reaching the ocean.",
      priority: 2
    },
    shabelle: {
      name: "Wabi Shebelle",
      role: "Eastern Artery",
      dest: "Somalia (Indian Ocean)",
      insight: "Critical water source for the Somali region. Often dries up before reaching the ocean.",
      priority: 2
    },
    wabi: {
      name: "Wabi Shebelle",
      role: "Eastern Artery",
      dest: "Somalia (Indian Ocean)",
      insight: "Critical water source for the Somali region. Often dries up before reaching the ocean.",
      priority: 2
    },
    genale: {
      name: "Genale-Dawa River",
      role: "Southern Frontier",
      dest: "Jubba River (Somalia)",
      insight: "Defines Ethiopia's southeastern border. Key to pastoral livelihoods in the Somali region.",
      priority: 2
    },
    dawa: {
      name: "Genale-Dawa River",
      role: "Southern Frontier",
      dest: "Jubba River (Somalia)",
      insight: "Defines Ethiopia's southeastern border. Key to pastoral livelihoods in the Somali region.",
      priority: 2
    },
    jubba: {
      name: "Genale-Dawa River",
      role: "Southern Frontier",
      dest: "Jubba River (Somalia)",
      insight: "Defines Ethiopia's southeastern border. Key to pastoral livelihoods in the Somali region.",
      priority: 2
    },
    juba: {
      name: "Genale-Dawa River",
      role: "Southern Frontier",
      dest: "Jubba River (Somalia)",
      insight: "Defines Ethiopia's southeastern border. Key to pastoral livelihoods in the Somali region.",
      priority: 2
    },
    baro: {
      name: "Baro-Akobo River",
      role: "Western Gateway",
      dest: "White Nile (South Sudan)",
      insight: "Ethiopia's only navigable river. Historic trade route to the Nile basin.",
      priority: 2
    },
    akobo: {
      name: "Baro-Akobo River",
      role: "Western Gateway",
      dest: "White Nile (South Sudan)",
      insight: "Ethiopia's only navigable river. Historic trade route to the Nile basin.",
      priority: 2
    },
    sobat: {
      name: "Baro-Akobo River",
      role: "Western Gateway",
      dest: "White Nile (South Sudan)",
      insight: "Ethiopia's only navigable river. Historic trade route to the Nile basin.",
      priority: 2
    },
    didessa: {
      name: "Didessa River",
      role: "Blue Nile Tributary",
      dest: "Abbay (Blue Nile)",
      insight: "Major tributary feeding the Blue Nile. Flows through the coffee-growing highlands of Wellega.",
      priority: 3
    },
    guder: {
      name: "Guder River",
      role: "Highland Tributary",
      dest: "Abbay (Blue Nile)",
      insight: "Flows from the Shewan highlands. Historic boundary marker in central Ethiopia.",
      priority: 3
    },
    muger: {
      name: "Muger River",
      role: "Highland Tributary",
      dest: "Abbay (Blue Nile)",
      insight: "Drains the northern Shewa highlands near Addis Ababa into the Blue Nile system.",
      priority: 3
    },
    jamma: {
      name: "Jamma River",
      role: "Highland Tributary",
      dest: "Abbay (Blue Nile)",
      insight: "Flows through deep gorges in the Ethiopian highlands. Historic barrier in Amhara region.",
      priority: 3
    },
    gilgel: {
      name: "Abbay (Blue Nile)",
      role: "Critical Resource",
      dest: "Mediterranean (via Sudan/Egypt)",
      insight: "Supplies 85% of the Nile's water. Historically defined the Kingdom of Gojjam as a natural fortress.",
      priority: 4
    },
    finchaa: {
      name: "Abbay (Blue Nile)",
      role: "Critical Resource",
      dest: "Mediterranean (via Sudan/Egypt)",
      insight: "Supplies 85% of the Nile's water. Historically defined the Kingdom of Gojjam as a natural fortress.",
      priority: 4
    },
    dabus: {
      name: "Abbay (Blue Nile)",
      role: "Critical Resource",
      dest: "Mediterranean (via Sudan/Egypt)",
      insight: "Major western tributary of the Blue Nile. Flows through lowland forests near the Sudan border.",
      priority: 3
    },
    anger: {
      name: "Abbay (Blue Nile)",
      role: "Critical Resource",
      dest: "Mediterranean (via Sudan/Egypt)",
      insight: "Supplies 85% of the Nile's water. Historically defined the Kingdom of Gojjam as a natural fortress.",
      priority: 4
    },
    mereb: {
      name: "Mereb-Gash River",
      role: "Border River",
      dest: "Terminates in Sudan",
      insight: "Marks the historic border between Ethiopia and Eritrea. Seasonal river critical to the lowlands.",
      priority: 2
    },
    gash: {
      name: "Mereb-Gash River",
      role: "Border River",
      dest: "Terminates in Sudan",
      insight: "Marks the historic border between Ethiopia and Eritrea. Seasonal river critical to the lowlands.",
      priority: 2
    },
    setit: {
      name: "Tekeze River",
      role: "Strategic Border",
      dest: "Atbarah River (Sudan)",
      insight: "Forms the natural boundary between Tigray and Eritrea. Historically a line of defense.",
      priority: 2
    },
    angereb: {
      name: "Tekeze River",
      role: "Strategic Border",
      dest: "Atbarah River (Sudan)",
      insight: "Forms the natural boundary between Tigray and Eritrea. Historically a line of defense.",
      priority: 4
    },
    weyna: {
      name: "Wabi Shebelle",
      role: "Eastern Artery",
      dest: "Somalia (Indian Ocean)",
      insight: "Critical water source for the Somali region. Often dries up before reaching the ocean.",
      priority: 4
    },
    fafen: {
      name: "Wabi Shebelle",
      role: "Eastern Artery",
      dest: "Somalia (Indian Ocean)",
      insight: "Critical water source for the Somali region. Often dries up before reaching the ocean.",
      priority: 4
    },
    erer: {
      name: "Wabi Shebelle",
      role: "Eastern Artery",
      dest: "Somalia (Indian Ocean)",
      insight: "Critical water source for the Somali region. Often dries up before reaching the ocean.",
      priority: 4
    }
  };

  // Default fallback - will be constructed dynamically based on path
  const createDefaultIntel = (stoppingFeature) => ({
    name: stoppingFeature || "Ethiopian River",
    role: "Tributary",
    dest: "Regional drainage system",
    insight: "Part of Ethiopia's complex river network that shapes the landscape and communities.",
    priority: 99
  });

  // Normalize river name for matching - remove common prefixes/suffixes and special chars
  const normalizeRiverName = (name) => {
    if (!name) return "";
    return name
      .toLowerCase()
      .replace(/river|stream|creek|tributary|wadi|wereda|woreda/gi, "")
      .replace(/[^a-z\s]/gi, "")
      .trim();
  };

  // Find the major Ethiopian river from the feature groups (fallback when spatial lookup fails)
  const findMajorRiver = (features, stoppingFeatureName) => {
    if (!features || features.length === 0) {
      return createDefaultIntel(stoppingFeatureName);
    }

    let bestMatch = null;
    let bestPriority = 999;

    // Search through all features for known major rivers
    for (const feature of features) {
      if (!feature.name) continue;

      const normalizedName = normalizeRiverName(feature.name);
      const words = normalizedName.split(/\s+/).filter(w => w.length > 2);

      // Check each key in RIVER_INTEL_DATA
      for (const [riverKey, riverData] of Object.entries(RIVER_INTEL_DATA)) {
        // Direct substring match
        if (normalizedName.includes(riverKey)) {
          if (riverData.priority < bestPriority) {
            bestMatch = riverData;
            bestPriority = riverData.priority;
          }
        }
        // Word-by-word match
        for (const word of words) {
          if (word.includes(riverKey) || riverKey.includes(word)) {
            if (riverData.priority < bestPriority) {
              bestMatch = riverData;
              bestPriority = riverData.priority;
            }
          }
        }
      }
    }

    // Also check the stopping feature name (the destination river)
    if (stoppingFeatureName) {
      const normalizedStopping = normalizeRiverName(stoppingFeatureName);
      const stoppingWords = normalizedStopping.split(/\s+/).filter(w => w.length > 2);

      for (const [riverKey, riverData] of Object.entries(RIVER_INTEL_DATA)) {
        if (normalizedStopping.includes(riverKey)) {
          if (riverData.priority < bestPriority) {
            bestMatch = riverData;
            bestPriority = riverData.priority;
          }
        }
        for (const word of stoppingWords) {
          if (word.includes(riverKey) || riverKey.includes(word)) {
            if (riverData.priority < bestPriority) {
              bestMatch = riverData;
              bestPriority = riverData.priority;
            }
          }
        }
      }
    }

    return bestMatch || createDefaultIntel(stoppingFeatureName);
  };

  // Helper: Check if a river name is "Unidentified"
  const isUnidentified = (name) => {
    if (!name) return true;
    return name.toLowerCase().includes('unidentified');
  };

  // Helper: Format a river name properly (capitalize)
  const formatRiverName = (name) => {
    if (!name) return null;
    return name
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  // Reactive: Get the clicked river name (first item in the API response)
  $: clickedRiverName = (() => {
    if (!featureGroups || featureGroups.length === 0) return null;
    return formatRiverName(featureGroups[0]?.name);
  })();

  // Reactive: Check if the clicked river is unidentified
  $: isClickedUnidentified = isUnidentified(clickedRiverName);

  // Reactive: Find the first NAMED (non-unidentified) river in the path
  $: firstNamedRiver = (() => {
    if (!featureGroups || featureGroups.length === 0) return null;
    for (const feature of featureGroups) {
      if (feature.name && !isUnidentified(feature.name)) {
        return formatRiverName(feature.name);
      }
    }
    return null;
  })();

  // Reactive: Get intel for the current river path (the "Basin Strategy")
  // Prefer spatial basin lookup over name matching
  $: riverIntel = basinIntel ? {
    name: basinIntel.name,
    role: basinIntel.role,
    dest: basinIntel.destination,
    insight: basinIntel.insight,
    priority: 1  // Spatial lookup is high priority
  } : findMajorRiver(featureGroups, currentStoppingFeature);
  $: isKnownRiver = riverIntel && riverIntel.priority < 99;

  // Reactive: Get the best river name (prefer proximity lookup over API response)
  $: identifiedRiverName = nearestRiver?.name || (!isClickedUnidentified ? clickedRiverName : null) || firstNamedRiver;

  // Reactive: Check if the identified river is a major basin river
  $: isMajorRiver = (() => {
    if (!identifiedRiverName || !riverIntel?.name) return false;
    const normalizedId = normalizeRiverName(identifiedRiverName);
    const normalizedBasin = normalizeRiverName(riverIntel.name);
    const idWords = normalizedId.split(/\s+/).filter(w => w.length > 2);
    const basinWords = normalizedBasin.split(/\s+/).filter(w => w.length > 2);
    return idWords.some(iw => basinWords.some(bw => iw.includes(bw) || bw.includes(iw)));
  })();

  // Reactive: Determine the display scenario
  // Scenario 1: No river identified → Show basin name + "Basin"
  // Scenario 2: Identified river is a main stem → Show river name + "MAIN STEM"
  // Scenario 3: Identified river is tributary → Show river name + "TRIBUTARY OF X"
  $: displayScenario = (() => {
    if (!identifiedRiverName) {
      return 'basin-only';
    }
    if (isMajorRiver) {
      return 'main-stem';
    }
    return 'named-tributary';
  })();

  // Reactive: Display name based on scenario
  $: displayName = (() => {
    if (displayScenario === 'basin-only') {
      const baseName = riverIntel?.name?.split('(')[0].trim();
      return baseName ? `${baseName} Basin` : 'Ethiopian River';
    }
    // For main-stem and named-tributary, show the identified river name
    return identifiedRiverName;
  })();

  // Reactive: Whether to show the subtitle
  $: showSubtitle = displayScenario === 'named-tributary' || displayScenario === 'main-stem';

  // Reactive: Subtitle text based on scenario
  $: subtitleText = (() => {
    if (displayScenario === 'named-tributary' && riverIntel?.name) {
      const baseName = riverIntel.name.split('(')[0].trim().toUpperCase();
      return `TRIBUTARY OF THE ${baseName} BASIN`;
    }
    if (displayScenario === 'main-stem') {
      return 'MAIN STEM';
    }
    return '';
  })();

  // Basin name for intel section label (when showing tributary)
  $: basinName = (() => {
    if (!riverIntel || !riverIntel.name) return '';
    const primaryName = riverIntel.name.split('(')[0].trim();
    return primaryName.toUpperCase();
  })();

  $: visible =
    vizState === "running" || vizState === "overview" || vizState === "loading" || vizState === "calculating"
      ? true
      : vizState === "uninitialized"
      ? false
      : visible;

  // Track if we're in a loading state (fetching new river data)
  $: isLoading = vizState === "loading" || vizState === "calculating";

  const dispatch = createEventDispatcher();

  const runNavigationPath = () => {
    dispatch("run-path");
  };

  const exitNavigationPath = () => {
    dispatch("exit-path");
  };

  const handleKeydown = (event) => {
    // Allow escape to close during overview or loading states
    if (event.key === "Escape" && (vizState === "overview" || vizState === "loading" || vizState === "calculating")) {
      exitNavigationPath();
    }
  };

  onMount(() => {
    const unsubscribeStoppingFeature = stoppingFeature.subscribe(
      (featureName) => {
        currentStoppingFeature = featureName;
      }
    );

    const unsubscribeStartLocation = startLocation.subscribe(
      (startLocation) => {
        currentStartLocation = startLocation;
      }
    );
  });
</script>

<svelte:window bind:innerWidth={screenWidth} on:keydown={handleKeydown} />

<div
  class="navbox-wrapper"
  style={`display: ${
    visible === true &&
    (screenWidth > 700 ||
      vizState === "overview" ||
      vizState === "loading" ||
      vizState === "calculating" ||
      (activeFeatureIndex >= 0 && featureGroups))
      ? "block"
      : "none"
  };`}
  tabindex="0"
  aria-label="Intelligence brief for river"
>
  <!-- Close Button - visible during overview and loading states -->
  <button
    class="close-button"
    style="display: {vizState === 'overview' || isLoading ? 'flex' : 'none'}"
    on:click={exitNavigationPath}
    title="Exit this path"
  >
    <img
      class="svg-icon-img"
      src="/images/x.svg"
      alt="exit this path"
    />
  </button>

  <!-- Loading State -->
  {#if isLoading}
    <div class="loading-state">
      <div class="loading-spinner"></div>
      <div class="loading-text">Tracing river path...</div>
    </div>

  <!-- Intelligence Brief Content (Desktop) -->
  {:else if screenWidth > 700}
    <div class="intel-brief">
      <!-- River Name Header (Local Reality - the specific river clicked) -->
      <h2 class="river-title">{displayName}</h2>

      <!-- Subtitle: Tributary relationship or Main Stem indicator -->
      <!-- Only show subtitle for named tributaries or main stem, NOT for unidentified rivers -->
      {#if showSubtitle && isKnownRiver}
        <div class="river-subtitle" class:tributary={displayScenario === 'named-tributary'} class:main-stem={displayScenario === 'main-stem'}>
          {subtitleText}
        </div>
      {/if}

      <!-- Stat Rows (only for known rivers) -->
      {#if isKnownRiver && riverIntel}
        <div class="stat-rows">
          <div class="stat-row">
            <span class="stat-icon">🌊</span>
            <span class="stat-label">Flows to:</span>
            <span class="stat-value">{riverIntel.dest || ''}</span>
          </div>
          <div class="stat-row">
            <span class="stat-icon">🛡️</span>
            <span class="stat-label">Strategic Role:</span>
            <span class="stat-value">{riverIntel.role || ''}</span>
          </div>
        </div>

        <!-- Basin Strategy Section (for named tributaries only - unidentified already shows as "X Tributary") -->
        {#if displayScenario === 'named-tributary'}
          <div class="basin-strategy-label">Basin Strategy</div>
        {/if}
      {/if}

      <!-- Insight Box -->
      <div class="insight-box">
        <p class="insight-text">{riverIntel?.insight || ''}</p>
      </div>

      <!-- Distance info -->
      {#if totalLength}
        <div class="distance-badge">
          {Math.round(totalLength)} km path
        </div>
      {/if}
    </div>

  <!-- Mobile: Simplified view -->
  {:else if activeFeatureIndex >= 0 && featureGroups}
    <div class="mobile-brief">
      <div class="river-title-mobile">{displayName}</div>
      <!-- Only show subtitle for named tributaries (unidentified rivers already have "Tributary" in title) -->
      {#if displayScenario === 'named-tributary' && isKnownRiver}
        <div class="river-subtitle-mobile">Tributary of {basinName}</div>
      {/if}
      <div class="insight-text-mobile">{riverIntel?.insight || ''}</div>
    </div>
  {/if}

  <!-- Running state close button -->
  {#if activeFeatureIndex >= 0 && vizState === "running"}
    <CloseButton on:abort-run />
  {/if}

  <!-- Pre-run Controls -->
  <div
    class="pre-run-controls"
    style="display: {vizState === 'overview' ? 'flex' : 'none'}"
  >
    {#if postRun}
      <button class="control-button start-button" on:click={runNavigationPath}>
        <img
          class="svg-icon-img"
          src="/images/repeat.svg"
          alt={"restart river run"}
          title={"Restart river run"}
        />
      </button>
    {:else}
      <button class="control-button fly-button" on:click={runNavigationPath}>
        <span class="fly-icon">✈️</span>
        <span class="fly-text">Fly Route</span>
      </button>
    {/if}
  </div>
</div>

<style type="text/scss">
  @import "../settings.scss";

  .navbox-wrapper {
    position: relative;
    width: 360px;
    max-width: 100%;
    background: rgba(10, 10, 12, 0.72);
    backdrop-filter: blur(16px);
    -webkit-backdrop-filter: blur(16px);
    border-radius: 14px;
    padding: 1.5rem;
    font-family: "Roboto", "Inter", Arial, Helvetica, sans-serif;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.35);
    border: 1px solid rgba(255, 255, 255, 0.1);
    transition: opacity 0.3s ease, transform 0.3s ease;
  }

  .close-button {
    position: absolute;
    top: 0.875rem;
    right: 0.875rem;
    width: 1.75rem;
    height: 1.75rem;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.08);
    border: 1px solid rgba(255, 255, 255, 0.12);
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease;
    padding: 0;
    z-index: 10;

    &:hover {
      background: rgba(239, 68, 68, 0.25);
      border-color: rgba(239, 68, 68, 0.4);
    }

    .svg-icon-img {
      height: 0.65rem;
      width: 0.65rem;
    }
  }

  // Loading State
  .loading-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 2rem 1rem;
    gap: 1rem;
  }

  .loading-spinner {
    width: 2.5rem;
    height: 2.5rem;
    border: 3px solid rgba(255, 255, 255, 0.15);
    border-top-color: #4facfe;
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }

  .loading-text {
    color: rgba(255, 255, 255, 0.6);
    font-size: 0.9rem;
    font-weight: 500;
    letter-spacing: 0.02em;
  }

  // Intelligence Brief Layout
  .intel-brief {
    padding-right: 1.5rem; // Space for close button
  }

  .river-title {
    margin: 0 0 0.25rem 0;
    font-size: 1.5rem;
    font-weight: 700;
    color: rgba(255, 255, 255, 0.95);
    line-height: 1.15;
    letter-spacing: 0.03em;
    text-transform: uppercase;
  }

  // River Subtitle (Tributary/Main Stem indicator)
  .river-subtitle {
    font-size: 0.7rem;
    font-weight: 600;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    margin-bottom: 1rem;

    &.tributary {
      color: #4facfe;
    }

    &.main-stem {
      color: rgba(255, 255, 255, 0.4);
    }
  }

  // Basin Strategy Label (shown above insight for tributaries)
  .basin-strategy-label {
    font-size: 0.65rem;
    font-weight: 600;
    color: rgba(79, 172, 254, 0.7);
    text-transform: uppercase;
    letter-spacing: 0.1em;
    margin-bottom: 0.4rem;
  }

  // Stat Rows
  .stat-rows {
    display: flex;
    flex-direction: column;
    gap: 0.6rem;
    margin-bottom: 1rem;
    padding-bottom: 1rem;
    border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  }

  .stat-row {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.9rem;
  }

  .stat-icon {
    font-size: 1rem;
    width: 1.25rem;
    text-align: center;
  }

  .stat-label {
    color: rgba(255, 255, 255, 0.5);
    font-weight: 500;
  }

  .stat-value {
    color: rgba(255, 255, 255, 0.9);
    font-weight: 500;
  }

  // Insight Box
  .insight-box {
    background: rgba(255, 255, 255, 0.04);
    border: 1px solid rgba(255, 255, 255, 0.06);
    border-radius: 8px;
    padding: 0.875rem 1rem;
    margin-bottom: 0.75rem;
  }

  .insight-text {
    margin: 0;
    font-size: 0.875rem;
    line-height: 1.55;
    color: rgba(255, 255, 255, 0.65);
    font-style: italic;
  }

  // Distance Badge
  .distance-badge {
    display: inline-block;
    font-size: 0.7rem;
    font-weight: 600;
    color: rgba(255, 255, 255, 0.4);
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  // Pre-run Controls
  .pre-run-controls {
    display: flex;
    gap: 0.5rem;
    margin-top: 1.25rem;
    padding-top: 1rem;
    border-top: 1px solid rgba(255, 255, 255, 0.08);
  }

  .control-button {
    border-radius: 8px;
    height: 2.25rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  // Ghost Button Style
  .fly-button {
    flex: 1;
    gap: 0.4rem;
    padding: 0 1rem;
    background: transparent;
    color: #4facfe;
    border: 1px solid rgba(79, 172, 254, 0.5);
    font-size: 0.85rem;

    &:hover {
      background: rgba(79, 172, 254, 0.1);
      border-color: rgba(79, 172, 254, 0.7);
    }

    &:active {
      background: rgba(79, 172, 254, 0.15);
    }

    .fly-icon {
      font-size: 0.95rem;
    }

    .fly-text {
      font-weight: 600;
      letter-spacing: 0.02em;
    }
  }

  .start-button {
    width: 2.25rem;
    background: transparent;
    border: 1px solid rgba(255, 255, 255, 0.15);

    &:hover {
      background: rgba(255, 255, 255, 0.08);
      border-color: rgba(255, 255, 255, 0.25);
    }
  }

  .svg-icon-img {
    height: 0.9rem;
    width: auto;
    filter: invert(1);
    opacity: 0.85;
  }

  // Mobile Brief
  .mobile-brief {
    text-align: center;
  }

  .river-title-mobile {
    font-size: 1.1rem;
    font-weight: 700;
    color: rgba(255, 255, 255, 0.95);
    text-transform: uppercase;
    letter-spacing: 0.03em;
    margin-bottom: 0.25rem;
  }

  .river-subtitle-mobile {
    font-size: 0.65rem;
    font-weight: 600;
    color: #4facfe;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    margin-bottom: 0.5rem;
  }

  .insight-text-mobile {
    font-size: 0.8rem;
    line-height: 1.45;
    color: rgba(255, 255, 255, 0.6);
    font-style: italic;
  }

  /* Shorter screens */
  @media only screen and (max-height: 900px) {
    .river-title {
      font-size: 1.3rem;
    }

    .stat-row {
      font-size: 0.85rem;
    }

    .insight-text {
      font-size: 0.8rem;
    }
  }

  /* Mobile */
  @media only screen and (max-width: $mobile-breakpoint) {
    .navbox-wrapper {
      position: absolute;
      z-index: 20;
      right: 1rem;
      left: 1rem;
      top: 16vh;
      width: auto;
      padding: 1.25rem;
    }

    .pre-run-controls {
      margin-top: 1rem;
      padding-top: 0.75rem;
    }

    .fly-button {
      flex: 1;
      padding: 0.6rem 1rem;
      height: 2.5rem;
    }

    .intel-brief {
      display: none;
    }
  }

  /* Tablet */
  @media only screen and (min-width: 701px) and (max-width: 1100px) {
    .navbox-wrapper {
      width: 320px;
    }

    .river-title {
      font-size: 1.3rem;
    }
  }
</style>
